// src/components/minigames/LibraryScreen.jsx
import React, { useState, useMemo, useCallback } from 'react';
import { alternarPantallaCompleta, hayPantallaCompleta } from './pantallaCompleta';
import apiClient from '../api/apiClient';
import { useCuratedLibrary } from './useCuratedLibrary';

const THUMB = id => `https://img.youtube.com/vi/${id}/mqdefault.jpg`;

//  3:22 · 30:52 · 1:06:14. La hora sólo aparece cuando la hay: escribir «0:03:22»
//  en una canción es ruido, y con vídeos de una hora «74:30» no se lee.
function relojDe(segundos) {
    if (typeof segundos !== 'number' || !isFinite(segundos) || segundos <= 0) return '';
    const s = Math.round(segundos);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    return h > 0
        ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
        : `${m}:${String(r).padStart(2, '0')}`;
}

const DIFF_COLOR = {
    basic: '#34d399', beginner: '#34d399',
    intermediate: '#60a5fa',
    advanced: '#fb923c',
    expert: '#f87171',
};

export default function LibraryScreen({ token, onStart }) {
    const { musicCategories, videoCategories, loading } = useCuratedLibrary(token);
    const [mode, setMode] = useState(null); // null | 'music' | 'doc'
    const [search, setSearch] = useState('');
    const [diffFilter, setDiff] = useState('');
    const [loading2, setLoad] = useState(false);
    const [error, setError] = useState('');

    const authToken = token || localStorage.getItem('token');

    // ── SOLO LO QUE SE PUEDE JUGAR ───────────────────────────────────────
    // Una pieza abre el reproductor si está CURADA y PUBLICADA en el CRM (trae
    // `curatedId`): es la que tiene su letra sincronizada o sus preguntas. Lo
    // demás era un catálogo de fondo que se pintaba con un candado y un
    // «Pronto», y lo único que hacía era ocupar sitio — tres pantallas de
    // tarjetas que no abren para llegar a las que sí.
    //
    // El servidor ya garantiza que todo lo que devuelve es jugable: descarta
    // las canciones sin `sync_data` y los vídeos sin preguntas aprovechables.
    // Aquí sólo se deja fuera lo que no viene de ahí.
    //
    // El `videoId` repetido se queda una sola vez: el mismo vídeo dado de alta
    // dos veces en el Command Center salía dos veces en la parrilla.
    const allItems = useMemo(() => {
        const cats = mode === 'music' ? musicCategories : videoCategories;
        const vistos = new Set();
        const salida = [];
        for (const cat of cats || []) {
            for (const item of cat.items || []) {
                if (!item.curatedId) continue;
                if (item.videoId && vistos.has(item.videoId)) continue;
                if (item.videoId) vistos.add(item.videoId);
                salida.push({ ...item, _cat: cat.id, _catLabel: cat.label });
            }
        }
        return salida;
    }, [mode, musicCategories, videoCategories]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return allItems.filter(item => {
            if (q && !(item.title || '').toLowerCase().includes(q)
                && !(item.artist || item.channel || '').toLowerCase().includes(q)) return false;
            if (diffFilter) {
                const d = mode === 'music' ? item.difficulty : item.level;
                if (d !== diffFilter) return false;
            }
            return true;
        });
    }, [allItems, search, diffFilter, mode]);

    const handleSelect = async (item) => {
        setLoad(true); setError('');
        try {
            const url = mode === 'music'
                ? `/api/minigames/cinema/library/songs/${item.curatedId}`
                : `/api/minigames/cinema/library/videos/${item.curatedId}`;
            const res = await apiClient.get(url);
            const detail = res.data;
            onStart({ ...detail, mode });
        } catch (e) {
            setError('Could not load this one. Try another.');
        }
        setLoad(false);
    };

    // Vive en `pantallaCompleta.js`: este bloque estaba copiado en tres
    // ficheros y el `catch` vacio dejaba al alumno pulsando un boton que no
    // hacia nada ni decia por que.
    const toggleFullscreen = () => { alternarPantallaCompleta(); };

    /* ── PIEZAS QUE YA NO ESTAN EN YOUTUBE ────────────────────────────────
       Se retiran del catálogo cuando su miniatura falla, pero SÓLO si alguna
       otra ha cargado: si falla la red fallan todas, y ocultarlas sin más
       dejaría el catálogo vacío — un problema de wifi disfrazado de
       biblioteca sin contenido.

       El 30-08-2026 se quitaron ocho del fichero por estar retiradas. Esto es
       para las que vengan: YouTube seguirá retirando vídeos. */
    const [retiradas, setRetiradas] = useState(() => new Set());
    const [youtubeResponde, setYoutubeResponde] = useState(false);

    const marcaMiniaturaOk = useCallback(() => setYoutubeResponde(true), []);
    const marcaMiniaturaRota = useCallback((videoId) => {
        if (!videoId) return;
        setRetiradas(prev => {
            if (prev.has(videoId)) return prev;
            const s = new Set(prev);
            s.add(videoId);
            return s;
        });
    }, []);

    // Lo que de verdad se pinta: lo filtrado menos lo que YouTube ya no sirve.
    const visibles = youtubeResponde
        ? filtered.filter(i => !retiradas.has(i.videoId))
        : filtered;
    const diffs = mode === 'music'
        ? ['basic', 'intermediate', 'advanced']
        : ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

    if (!mode) {
        const cardStyle = {
            flex: '1 1 300px',
            background: 'var(--nc-surface)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: 24,
            padding: '40px 30px',
            cursor: 'pointer',
            textAlign: 'center',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            position: 'relative',
            overflow: 'hidden'
        };

        return (
            <div className="ls-root" style={{ alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <style>{`
                    .nc-home-card:hover {
                        transform: translateY(-8px) scale(1.02);
                        border-color: rgba(139, 92, 246, 0.6) !important;
                        box-shadow: 0 20px 40px rgba(0,0,0,0.6), 0 0 40px rgba(139, 92, 246, 0.2) !important;
                    }
                    .nc-home-card::before {
                        content: '';
                        position: absolute;
                        top: 0; left: 0; right: 0; height: 4px;
                        background: linear-gradient(90deg, #8b5cf6, #06b6d4);
                        opacity: 0;
                        transition: opacity 0.3s;
                    }
                    .nc-home-card:hover::before {
                        opacity: 1;
                    }
                `}</style>
                {/* En un movil este boton absoluto se montaba encima del emoji del
                    titulo, que va centrado: los dos ocupaban la misma franja y
                    se solapaban. Se separa del borde con `clamp` y se le da su
                    propia capa para que quede claro cual esta encima. */}
                {/* Solo donde el navegador puede. En el Safari del iPhone,
                    `requestFullscreen` sobre un elemento no existe —solo sobre un
                    <video>— asi que este boton no hacia NADA y ademas se tragaba el
                    error. Un boton muerto que no explica por que enseña al alumno que
                    la app no responde, y deja de fiarse de los demas botones. */}
                {hayPantallaCompleta() && (
                    <button className="ls-mode-btn ls-btn-pantalla" onClick={toggleFullscreen} style={{ position: 'absolute', top: 'clamp(8px, 2.5vw, 20px)', right: 'clamp(8px, 2.5vw, 20px)', padding: 'clamp(6px, 2vw, 10px) clamp(9px, 3vw, 14px)', zIndex: 3 }}>
                        ⛶ Pantalla Completa
                    </button>
                )}
                {/* ── POR QUE ESTO USA clamp() Y NO NUMEROS ────────────────────
                    Aqui habia `fontSize: 72`, `fontSize: 48` y `marginTop: -40`
                    escritos a pelo. Un estilo inline GANA a cualquier media
                    query, asi que esta portada no se podia adaptar a un movil
                    por CSS ni queriendo: el titulo ocupaba 48px de alto en una
                    pantalla de 390 y empujaba las tarjetas fuera de la vista.

                    Con `clamp(minimo, fluido, maximo)` el navegador interpola
                    solo. En un telefono de 360px el titulo baja a 30px; en un
                    portatil se queda en los 48 de siempre. Nada que mantener en
                    dos sitios. */}
                <div style={{ textAlign: 'center', marginBottom: 'clamp(20px, 5vw, 50px)', marginTop: 0 }}>
                    <div style={{ fontSize: 'clamp(42px, 12vw, 72px)', marginBottom: 'clamp(8px, 2vw, 15px)', filter: 'drop-shadow(0 0 20px rgba(139,92,246,0.4))' }}>🎬</div>
                    <h1 style={{
                        fontSize: 'clamp(30px, 9vw, 48px)', fontWeight: 900, fontFamily: 'Inter, sans-serif', margin: 0,
                        background: 'linear-gradient(135deg, #c4b5fd, #8b5cf6, #06b6d4)',
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.03em', lineHeight: 1.05
                    }}>
                        NeuraCinema
                    </h1>
                    <p style={{ fontSize: 'clamp(13px, 3.6vw, 16px)', color: 'var(--nc-txt2)', marginTop: 'clamp(6px, 2vw, 12px)', letterSpacing: 1 }}>
                        Pick your immersive experience
                    </p>
                </div>
                
                <div style={{ display: 'flex', gap: 'clamp(14px, 4vw, 30px)', maxWidth: 840, width: '100%', padding: '0 clamp(10px, 4vw, 20px)', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {/* Música Card */}
                    <div className="nc-home-card" onClick={() => setMode('music')} style={cardStyle}>
                        <div style={{ fontSize: 56, marginBottom: 20, filter: 'drop-shadow(0 0 15px rgba(139,92,246,0.5))' }}>🎵</div>
                        <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 12px', color: '#fff', fontFamily: 'Inter, sans-serif' }}>Music</h2>
                        <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, margin: 0 }}>
                            Train your listening with great songs. Catch every word at the pace of the artists you love.
                        </p>
                    </div>

                    {/* Video Card */}
                    <div className="nc-home-card" onClick={() => setMode('doc')} style={cardStyle}>
                        <div style={{ fontSize: 56, marginBottom: 20, filter: 'drop-shadow(0 0 15px rgba(6,182,212,0.5))' }}>🌍</div>
                        <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 12px', color: '#fff', fontFamily: 'Inter, sans-serif' }}>Video</h2>
                        <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, margin: 0 }}>
                            An immersive world of hand-picked videos: science, technology, culture and much more.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ls-root">
            {/* Header */}
            <div className="ls-header">
                <button className="ls-mode-btn" onClick={() => setMode(null)} style={{ padding: '6px 12px', marginRight: 10 }}>
                    ← Inicio
                </button>
                <div className="ls-logo">🎬 NeuraCinema</div>
                <div className="ls-mode-toggle">
                    <button className={`ls-mode-btn ${mode === 'music' ? 'active' : ''}`} onClick={() => { setMode('music'); setDiff(''); }}>
                        🎵 Music
                    </button>
                    <button className={`ls-mode-btn ${mode === 'doc' ? 'active' : ''}`} onClick={() => { setMode('doc'); setDiff(''); }}>
                        🎓 Videos
                    </button>
                    {/* Solo donde el navegador puede. En el Safari del iPhone,
                    `requestFullscreen` sobre un elemento no existe —solo sobre un
                    <video>— asi que este boton no hacia NADA y ademas se tragaba el
                    error. Un boton muerto que no explica por que enseña al alumno que
                    la app no responde, y deja de fiarse de los demas botones. */}
                    {hayPantallaCompleta() && (
                        <button className="ls-mode-btn" onClick={toggleFullscreen} style={{ padding: '8px 12px', marginLeft: 8 }}>
                            ⛶
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="ls-filters">
                <div className="ls-search-wrap">
                    <span>🔍</span>
                    <input
                        maxLength={80}
                        className="ls-search"
                        placeholder={mode === 'music' ? 'Search song or artist…' : 'Search video or channel…'}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {search && <button className="ls-clear" onClick={() => setSearch('')}>✕</button>}
                </div>
                <div className="ls-chips">
                    <button className={`ls-chip ${!diffFilter ? 'active' : ''}`} onClick={() => setDiff('')}>All</button>
                    {diffs.map(d => (
                        <button key={d} className={`ls-chip ${diffFilter === d ? 'active' : ''}`}
                            style={{ '--cc': DIFF_COLOR[d] || '#60a5fa' }}
                            onClick={() => setDiff(diffFilter === d ? '' : d)}>
                            {d}
                        </button>
                    ))}
                </div>
                {/* ── AQUÍ HABÍA UNA SEGUNDA FILA DE FILTROS, POR CATEGORÍA ───
                    Se quitó el 05-10-2026. No se pintaba la categoría bonita
                    («Ciencia», «IA & Tecnología») sino el campo «Tema» tal cual
                    está escrito en el Command Center, y ahí hay temas que son
                    una frase entera: «Astronomy, the Fermi Paradox and the
                    search for extraterrestrial life». Diecisiete botones de ese
                    tamaño ocupaban cuatro renglones y empujaban el catálogo
                    fuera de la pantalla — justo lo contrario de lo que hace un
                    filtro.

                    El buscador ya cubre lo mismo y mejor: busca en el título y
                    en el canal sin obligar a nadie a leer diecisiete etiquetas.
                    Si algún día vuelven, lo que hay que arreglar primero es el
                    dato: el «Tema» tiene que ser una palabra, no un resumen. */}
                {error && <div className="ls-error">⚠️ {error}</div>}
            </div>

            {/* Grid */}
            <div className="ls-content">
                {loading && <div className="ls-loading"><div className="ls-spinner" /><span>Loading library…</span></div>}
                {/* Dos vacíos distintos, y decirlo importa: «no hay nada todavía»
                    se arregla publicando en el Command Center, y «no hay nada
                    CON ESTO» se arregla borrando el filtro. Antes los dos decían
                    «prueba otros filtros», que con la biblioteca vacía manda a
                    buscar algo que no existe. */}
                {!loading && visibles.length === 0 && (
                    <div className="ls-empty">
                        <div style={{ fontSize: 48 }}>{mode === 'music' ? '🎵' : '🎬'}</div>
                        {allItems.length === 0
                            ? <p>Todavía no hay {mode === 'music' ? 'canciones' : 'videos'} publicados aquí. Vuelve pronto.</p>
                            : <p>Sin resultados. Prueba otros filtros.</p>}
                    </div>
                )}
                {!loading && (
                    <div className="ls-grid">
                        {visibles.map((item, i) => (
                            <LibraryCard
                                key={`db-${item.curatedId}`}
                                item={item}
                                mode={mode}
                                loading={loading2}
                                onClick={() => handleSelect(item)}
                                onMiniaturaOk={marcaMiniaturaOk}
                                onMiniaturaRota={marcaMiniaturaRota}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

//  Ya no lleva etiqueta de estado: todo lo que llega aquí se puede jugar, así
//  que un «Lista» en cada tarjeta no distingue nada — sólo tapaba la miniatura.
function LibraryCard({ item, mode, loading, onClick, onMiniaturaRota, onMiniaturaOk }) {
    const diff = mode === 'music' ? item.difficulty : item.level;
    const sub = mode === 'music' ? item.artist : item.channel;
    const color = DIFF_COLOR[diff] || '#60a5fa';
    const reloj = relojDe(item.duracion);

    return (
        <div className="ls-card" onClick={onClick} style={{ '--dc': color }}>
            <div className="ls-card-thumb">
                {/* La miniatura es el aviso de que el vídeo sigue en YouTube:
                    cuando se retira, `mqdefault.jpg` pasa a 404. Antes esto
                    sólo escondía la imagen y dejaba la tarjeta, así que el
                    alumno la pulsaba y el reproductor no arrancaba. */}
                <img src={THUMB(item.videoId)} alt={item.title}
                    onLoad={() => onMiniaturaOk && onMiniaturaOk()}
                    onError={e => {
                        e.target.style.display = 'none';
                        if (onMiniaturaRota) onMiniaturaRota(item.videoId);
                    }} />
                <div className="ls-card-play">{loading ? '⏳' : '▶'}</div>
                <div className="ls-card-diff" style={{ color, borderColor: color }}>{diff}</div>
                {/* Abajo a la derecha, como en cualquier reproductor: es donde
                    se busca la duración sin tener que leer la tarjeta. */}
                {reloj && <div className="ls-card-reloj">🕐 {reloj}</div>}
            </div>
            <div className="ls-card-body">
                <div className="ls-card-title">{item.title}</div>
                <div className="ls-card-sub">{sub}</div>
                {/* Aquí iba el «Tema» del Command Center —TECH, GENERAL, o una
                    frase de doce palabras—. Ahora dice qué es la pieza, que es
                    un dato que no depende de cómo se haya escrito nada. */}
                <div className="ls-card-cat">{mode === 'music' ? '🎵 Song' : '🎬 Video'}</div>
            </div>
        </div>
    );
}